// src/pages/auth/HostPasswordSetup.tsx
import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {supabase} from '@/services/supabase';
import {LoadingSpinner} from '@/components/ui';
import {useAuth} from '@/context/AuthContext';
import {Eye, EyeOff, Lock, CheckCircle} from 'lucide-react';
import toast from 'react-hot-toast';

interface PasswordSetupProps {
    reason?: 'initial_setup' | 'admin_required' | 'security_update';
    userType?: 'host' | 'admin';
    redirectPath?: string;
    clearPasswordResetFunction?: string;
    title?: string;
}

const PasswordSetup: React.FC<PasswordSetupProps> = ({
                                                         reason = 'security_update',
                                                         userType = 'host',
                                                         redirectPath = '/host-dashboard',
                                                         clearPasswordResetFunction = 'clear_host_password_reset',
                                                         title
                                                     }) => {
    const navigate = useNavigate();
    const {user, refreshUser} = useAuth();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    // Password strength validation
    const [passwordStrength, setPasswordStrength] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false
    });

    useEffect(() => {
        // Validate password strength as user types
        setPasswordStrength({
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        });
    }, [password]);

    const getReasonMessage = () => {
        switch (reason) {
            case 'initial_setup':
                return `Welcome! Please set up your password to access your ${userType} dashboard.`;
            case 'admin_required':
                return 'Your administrator has requested that you update your password.';
            case 'security_update':
                return 'For your security, please set up a new password to continue.';
            default:
                return 'Please set up your password to continue.';
        }
    };

    const isPasswordValid = () => {
        return Object.values(passwordStrength).every(Boolean) &&
            password === confirmPassword;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isPasswordValid()) {
            toast.error('Please ensure password meets all requirements and passwords match');
            return;
        }

        if (!user?.email) {
            toast.error('User session not found. Please try again.');
            return;
        }

        setIsLoading(true);

        try {
            // Update password using Supabase auth
            const {error: passwordError} = await supabase.auth.updateUser({
                password: password
            });

            if (passwordError) {
                throw passwordError;
            }

            // For admins, update the admin_users.id to match auth user ID after password setup
            if (userType === 'admin') {
                console.log('🔧 Updating admin_users.id to match auth user ID after password setup');
                const {data: {user: authUser}} = await supabase.auth.getUser();
                if (authUser && user.id !== authUser.id) {
                    const {error: updateIdError} = await supabase
                        .from('admin_users')
                        .update({id: authUser.id})
                        .eq('email', user.email);

                    if (updateIdError) {
                        console.error('Failed to update admin ID:', updateIdError);
                        throw new Error('Failed to complete account setup');
                    }
                    console.log('✅ Admin ID updated to match auth user ID');
                }
            }

            // Clear the password reset requirement using the provided function
            const parameterName: string = userType === 'host' ? 'p_host_email' : 'p_admin_email';
            const rpcParams = {[parameterName]: user.email};

            const {error: clearResetError} = await supabase.rpc(clearPasswordResetFunction, rpcParams);

            if (clearResetError) {
                console.warn('Failed to clear password reset flag:', clearResetError);
                // Don't throw here - password was updated successfully
            }

            toast.success('Password updated successfully! Redirecting to dashboard...');

            // Refresh user context to get updated data
            await refreshUser();

            // Navigate to the specified dashboard
            navigate(redirectPath, {replace: true});

        } catch (error: any) {
            console.error('Password setup error:', error);
            toast.error(error.message || 'Failed to update password');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await supabase.auth.signOut();
            navigate('/login', {replace: true});
        } catch (error) {
            console.error('Sign out error:', error);
            // Force redirect even on error
            window.location.href = '/login';
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                {/* Header */}
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Lock className="h-8 w-8 text-white"/>
                    </div>
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                        {title || `${userType === 'host' ? 'Host' : 'Admin'} Password Setup Required`}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        {getReasonMessage()}
                    </p>
                    {user?.email && (
                        <p className="mt-1 text-sm text-gray-500">
                            Account: {user.email}
                        </p>
                    )}
                </div>

                {/* Password Setup Form */}
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Enter your new password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400"/>
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400"/>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Password Requirements */}
                        {password && (
                            <div className="bg-gray-50 p-3 rounded-md">
                                <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                                <ul className="space-y-1 text-sm">
                                    {[
                                        {
                                            key: 'minLength',
                                            text: 'At least 8 characters',
                                            met: passwordStrength.minLength
                                        },
                                        {
                                            key: 'hasUpperCase',
                                            text: 'One uppercase letter',
                                            met: passwordStrength.hasUpperCase
                                        },
                                        {
                                            key: 'hasLowerCase',
                                            text: 'One lowercase letter',
                                            met: passwordStrength.hasLowerCase
                                        },
                                        {key: 'hasNumber', text: 'One number', met: passwordStrength.hasNumber},
                                        {
                                            key: 'hasSpecialChar',
                                            text: 'One special character',
                                            met: passwordStrength.hasSpecialChar
                                        }
                                    ].map(({key, text, met}) => (
                                        <li key={key}
                                            className={`flex items-center space-x-2 ${met ? 'text-green-600' : 'text-gray-500'}`}>
                                            <CheckCircle
                                                className={`h-4 w-4 ${met ? 'text-green-600' : 'text-gray-300'}`}/>
                                            <span>{text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Confirm Password Field */}
                        <div>
                            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                                Confirm Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="confirm-password"
                                    name="confirm-password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Confirm your password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400"/>
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400"/>
                                    )}
                                </button>
                            </div>
                            {confirmPassword && password !== confirmPassword && (
                                <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div>
                        <button
                            type="submit"
                            disabled={isLoading || !isPasswordValid()}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <LoadingSpinner size="sm" className="mr-2"/>
                                    Updating Password...
                                </>
                            ) : (
                                'Update Password & Continue'
                            )}
                        </button>
                    </div>

                    {/* Sign Out Option */}
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={handleSignOut}
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                            Sign out instead
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PasswordSetup;
